/**
 * ADR-118 — auth state machine + result types.
 *
 * Pure types and a reducer. No React, no DOM. Everything in this
 * file is testable as plain JS — load it under Vitest without any
 * provider mounting.
 *
 * The state machine is a discriminated union so consumers can
 * pattern-match exhaustively:
 *
 *   const banner = (() => {
 *     switch (state.status) {
 *       case 'anonymous':       return null;
 *       case 'authenticating':  return <Spinner />;
 *       case 'authenticated':   return <UserMenu user={state.user} />;
 *       case 'refreshing':      return <UserMenu user={state.user} />;
 *       case 'expired':         return <ReauthBanner />;
 *       case 'logged-out':      return null;
 *     }
 *   })();
 *
 * Tokens (`jwt`, `refreshToken`) are kept generic strings; cookie
 * vs. localStorage transport is handled in the storage / cookie
 * layers. The user shape `U` is generic — apps
 * inject their own (ONE: 4-field, AIM/SMS/AIR: rich).
 */

/**
 * Generic user-shape constraint. Every adopter's user type must at
 * minimum carry an `id` and an `email` so the lib can identify the
 * authenticated principal for telemetry / boot-time reconciliation.
 * Beyond those two, layers above (auth-permissions, auth-organization)
 * may require additional fields — they document their own constraints.
 */
export interface BaseUser {
  id: number | string;
  email: string;
  /**
   * The user's preferred UI language, as an ISO-639-1 code (optionally with a
   * region subtag). Maps to `BaseUser.language` on the Django side — the same
   * column SCIM's `preferredLanguage` attribute writes through. Empty/null
   * means "no preference stored", which falls back to the district default.
   */
  language?: string | null;
}

/**
 * The discriminated-union auth state. The `status` field is the
 * discriminator; consumers MUST pattern-match exhaustively.
 *
 * Token fields:
 *   - `jwt`: short-lived access token. Required for `authenticated`
 *     and `refreshing` states.
 *   - `refreshToken`: long-lived refresh token. Optional — some
 *     adapters (allauth session-cookie) don't expose one.
 */
export type AuthState<U extends BaseUser = BaseUser> =
  | { status: 'anonymous' }
  | { status: 'authenticating' }
  | {
    status: 'authenticated';
    user: U;
    jwt: string;
    refreshToken?: string;
  }
  | {
    status: 'refreshing';
    user: U;
    jwt: string;
    refreshToken?: string;
  }
  | { status: 'expired'; user: U }
  | { status: 'logged-out' };

/**
 * Action discriminated-union driving the reducer. Adapters dispatch
 * these; consumers never construct them directly.
 */
export type AuthAction<U extends BaseUser = BaseUser> =
  | { type: 'login_start' }
  | {
    type: 'login_success';
    user: U;
    jwt: string;
    refreshToken?: string;
  }
  | { type: 'login_failure' }
  | { type: 'refresh_start' }
  | { type: 'refresh_success'; jwt: string; refreshToken?: string }
  | { type: 'refresh_failure' }
  | { type: 'expire' }
  | { type: 'logout' }
  | { type: 'hydrate'; state: AuthState<U> }
  /**
   * Replace the user object on the existing state without
   * disturbing the token / status. Used by `libs/auth-permissions` to
   * push a freshly-fetched user (with updated `permissions`) into the
   * provider after the backend bumps ``permissions_version``.
   *
   * Only valid when the state has a user (`authenticated`, `refreshing`,
   * `expired`). In any other state the reducer ignores it — the
   * canonical way to *set* a user is via `login_success` / `hydrate`.
   */
  | { type: 'set_user'; user: U };

/**
 * Canonical login-result shape after an adapter resolves. Adapters
 * normalize their backend-specific responses into this. The lib
 * dispatches `login_success` with the unpacked fields.
 */
export interface LoginResult<U extends BaseUser = BaseUser> {
  user: U;
  jwt: string;
  refreshToken?: string;
  /**
   * Bumped-on-change counter for permission-version cache invalidation.
   * Only set by adapters whose backends emit this (SimpleJWT does;
   * allauth doesn't — leaves it `undefined`).
   * See ADR-118 §Cross-platform auth wire contract for semantics.
   */
  permissionsVersion?: number;
}

/**
 * Canonical logout-result shape. Trivially small today; reserved
 * for future expansion (e.g. when the server-side token blocklist
 * lands and the response carries a `revokedAt` timestamp).
 */
export interface LogoutResult {
  ok: boolean;
}

/**
 * The lib's initial state. Always `anonymous`. Consumers must
 * dispatch `hydrate` from boot-time reconciliation
 * before any UI relies on `state.user`.
 */
export const initialAuthState: AuthState = { status: 'anonymous' };
